import { useRef } from 'react'
import { toast } from 'sonner'
import { FileSignature, FileText, Image as ImageIcon, Paperclip, Receipt, X } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { validateDocFile } from './chat-utils'

const ModeTabs = ({ mode, onChange, activeColor }) => (
  <div className="flex gap-2 mb-4">
    <button
      type="button"
      onClick={() => onChange('structured')}
      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
        mode === 'structured'
          ? `${activeColor} text-white border-transparent`
          : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
      }`}
    >
      Saisir manuellement
    </button>
    <button
      type="button"
      onClick={() => onChange('file')}
      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
        mode === 'file'
          ? `${activeColor} text-white border-transparent`
          : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
      }`}
    >
      Joindre un fichier
    </button>
  </div>
)

const FilePicker = ({ file, onPick, onClear, disabled, accentClass }) => {
  const inputRef = useRef(null)

  const handleChange = (e) => {
    const picked = e.target.files?.[0]
    if (!picked) return
    const err = validateDocFile(picked)
    if (err) {
      toast.error(err)
      e.target.value = ''
      return
    }
    onPick(picked)
    e.target.value = ''
  }

  if (file) {
    return (
      <div className={`flex items-center gap-3 p-3 rounded-lg border ${accentClass} bg-slate-50`}>
        <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
          {file.type === 'application/pdf' ? (
            <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
          ) : (
            <ImageIcon className="w-5 h-5 text-[hsl(var(--primary))]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">{file.name}</p>
          <p className="text-xs text-slate-500">
            {file.size > 0 ? `${(file.size / 1024).toFixed(0)} Ko` : '—'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className="p-1.5 rounded-lg hover:bg-white text-slate-500"
          aria-label="Supprimer le fichier"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-2 p-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors"
      >
        <Paperclip className="w-5 h-5 text-[hsl(var(--primary))]" />
        Choisir un fichier
      </button>
    </>
  )
}

export function ContractFormDialog({
  open,
  onOpenChange,
  form,
  onFormChange,
  inputMode,
  onInputModeChange,
  file,
  onFileChange,
  onSubmit,
  isBusy,
}) {
  const titleOk = form.title.trim().length >= 3
  const canSubmit =
    inputMode === 'structured'
      ? titleOk && !!form.amount.trim()
      : titleOk && !!file

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-[hsl(var(--primary))]" />
            Envoyer un contrat
          </DialogTitle>
        </DialogHeader>

        <ModeTabs
          mode={inputMode}
          onChange={onInputModeChange}
          activeColor="bg-[hsl(var(--primary))]"
        />

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Titre du contrat</label>
            <Input
              value={form.title}
              onChange={(e) => onFormChange({ ...form, title: e.target.value })}
              placeholder="Ex: Rénovation salle de bain"
            />
          </div>

          {inputMode === 'structured' ? (
            <>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => onFormChange({ ...form, description: e.target.value })}
                  placeholder="Détails du contrat..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Montant (€)</label>
                  <Input
                    value={form.amount}
                    onChange={(e) => onFormChange({ ...form, amount: e.target.value })}
                    placeholder="5000"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Durée</label>
                  <Input
                    value={form.duration}
                    onChange={(e) => onFormChange({ ...form, duration: e.target.value })}
                    placeholder="2 semaines"
                  />
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Fichier (PDF, JPG ou PNG · 10 Mo max)
              </label>
              <FilePicker
                file={file}
                onPick={onFileChange}
                onClear={() => onFileChange(null)}
                disabled={isBusy}
                accentClass="border-[hsl(var(--primary))]/40"
              />
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || isBusy}
          className="btn-clickpro-primary w-full mt-4"
        >
          <FileSignature className="w-4 h-4 mr-2" />
          {isBusy ? 'Envoi en cours…' : 'Envoyer le contrat'}
        </Button>
      </DialogContent>
    </Dialog>
  )
}

export function QuoteFormDialog({
  open,
  onOpenChange,
  mode,
  form,
  onFormChange,
  inputMode,
  onInputModeChange,
  file,
  onFileChange,
  onSubmit,
  isBusy,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
}) {
  const isQuote = mode === 'QUOTE'
  const activeColor = isQuote ? 'bg-emerald-600' : 'bg-amber-500'
  const titleOk = form.title.trim().length >= 3
  const validItems = form.items.filter((item) => item.label.trim() && item.amount.trim())
  const canSubmit =
    inputMode === 'structured'
      ? titleOk && !!form.total.trim() && validItems.length > 0
      : titleOk && !!file

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className={`w-5 h-5 ${isQuote ? 'text-emerald-600' : 'text-amber-500'}`} />
            {isQuote ? 'Envoyer un devis' : 'Envoyer une facture'}
          </DialogTitle>
        </DialogHeader>

        <ModeTabs mode={inputMode} onChange={onInputModeChange} activeColor={activeColor} />

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Titre</label>
            <Input
              value={form.title}
              onChange={(e) => onFormChange({ ...form, title: e.target.value })}
              placeholder={isQuote ? 'Ex: Devis peinture' : 'Ex: Facture fin de travaux'}
            />
          </div>

          {inputMode === 'structured' ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Lignes</label>
                {form.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      value={item.label}
                      onChange={(e) => onUpdateItem(index, 'label', e.target.value)}
                      placeholder="Libellé"
                      className="flex-1"
                    />
                    <Input
                      value={item.amount}
                      onChange={(e) => onUpdateItem(index, 'amount', e.target.value)}
                      placeholder="Montant"
                      type="number"
                      min="0"
                      className="w-28"
                    />
                    {form.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => onRemoveItem(index)}
                        className="p-2 text-slate-400 hover:text-red-500"
                        aria-label="Supprimer la ligne"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={onAddItem}>
                  Ajouter une ligne
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Total (€)</label>
                  <Input
                    value={form.total}
                    onChange={(e) => onFormChange({ ...form, total: e.target.value })}
                    placeholder="1500"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">
                    {isQuote ? 'Valide jusqu\'au' : 'Échéance'}
                  </label>
                  <Input
                    value={form.validUntil}
                    onChange={(e) => onFormChange({ ...form, validUntil: e.target.value })}
                    placeholder="JJ/MM/AAAA"
                  />
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Fichier (PDF, JPG ou PNG · 10 Mo max)
              </label>
              <FilePicker
                file={file}
                onPick={onFileChange}
                onClear={() => onFileChange(null)}
                disabled={isBusy}
                accentClass={isQuote ? 'border-emerald-300' : 'border-amber-300'}
              />
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || isBusy}
          className={`w-full mt-4 ${isQuote ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
        >
          <Receipt className="w-4 h-4 mr-2" />
          {isBusy ? 'Envoi en cours…' : isQuote ? 'Envoyer le devis' : 'Envoyer la facture'}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
